import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { getOrCreateDeviceId, computeFingerprint } from "@/lib/clientHints";
import { toast } from "sonner";
import { Laptop, Plus, Check, XCircle, Trash2, ShieldCheck, Hash } from "lucide-react";

export default function DevicesPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const isAdmin = user?.role === "admin";
  const [devices, setDevices] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_email: "", name: "", mac_address: "", notes: "" });

  const load = async () => {
    const { data } = await api.get("/devices");
    setDevices(data);
    if (isAdmin) {
      try {
        const u = await api.get("/users");
        setUsers(u.data);
      } catch {}
    }
  };
  useEffect(() => { load(); }, []);

  const registerCurrent = async () => {
    const device_id = getOrCreateDeviceId();
    const fingerprint = computeFingerprint();
    try {
      const { data } = await api.post("/devices/register", {
        device_id, fingerprint, name: `${navigator.platform} · ${user?.name}`,
      });
      toast.success(`Terminal enregistré (statut: ${data.status})`);
      load();
    } catch (err) {
      toast.error("Erreur enregistrement");
    }
  };

  const createDeclared = async () => {
    if (!form.user_email || !form.name) {
      toast.error("Email + nom requis"); return;
    }
    try {
      await api.post("/devices", form);
      toast.success("Terminal pré-déclaré (attend la première connexion de l'utilisateur)");
      setOpen(false);
      setForm({ user_email: "", name: "", mac_address: "", notes: "" });
      load();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const approve = async (id) => {
    await api.post(`/devices/${id}/approve`);
    toast.success("Terminal approuvé");
    load();
  };
  const revoke = async (id) => {
    if (!window.confirm("Révoquer ce terminal ?")) return;
    await api.post(`/devices/${id}/revoke`);
    toast.success("Terminal révoqué");
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer définitivement ce terminal ?")) return;
    await api.delete(`/devices/${id}`);
    load();
  };

  const currentDeviceId = getOrCreateDeviceId();

  return (
    <div className="space-y-6" data-testid="devices-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">
            Gestion des terminaux
          </div>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">Terminaux enrôlés</h1>
          <p className="text-xs text-white/50 mt-1">
            Un terminal doit être approuvé pour être considéré comme <span className="text-cyan-400">géré par l'organisation</span>.
            Le fingerprint remplace la MAC (inaccessible depuis un navigateur pour raisons de vie privée).
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={registerCurrent}
                  data-testid="enroll-current-btn"
                  className="bg-white/10 hover:bg-white/20 text-white">
            <ShieldCheck className="h-4 w-4" /> Enrôler ce terminal
          </Button>
          {isAdmin && (
            <Button onClick={() => setOpen(true)}
                    data-testid="declare-device-btn"
                    className="bg-cyan-500 text-black hover:bg-cyan-400">
              <Plus className="h-4 w-4" /> Pré-déclarer (MAC)
            </Button>
          )}
        </div>
      </div>

      <Card className="bg-cyan-500/5 border-cyan-500/25 rounded-sm p-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-300 flex items-center gap-2">
          <Hash className="h-3 w-3" /> Terminal courant
        </div>
        <div className="mt-1 text-xs font-mono text-white/80">{currentDeviceId}</div>
      </Card>

      <Card className="bg-white/[0.02] border-white/10 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-white/10 bg-white/[0.02]">
            <tr className="text-left text-white/50 uppercase tracking-[0.2em] text-[10px]">
              <th className="p-3">Nom / OS</th>
              <th className="p-3">Utilisateur</th>
              <th className="p-3">Device ID</th>
              <th className="p-3">MAC déclarée</th>
              <th className="p-3">Statut</th>
              <th className="p-3">Enrôlé</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {devices.length === 0 && (
              <tr><td colSpan={7} className="text-center text-white/40 py-8">
                Aucun terminal enrôlé.
              </td></tr>
            )}
            {devices.map((d) => (
              <tr key={d._id} className="border-b border-white/5 hover:bg-white/[0.02]"
                  data-testid={`device-row-${d._id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <Laptop className="h-4 w-4 text-cyan-400" />
                    <div>
                      <div>{d.name || "—"}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3 font-mono text-xs text-white/70">{d.user_email}</td>
                <td className="p-3 font-mono text-[10px] text-white/50">
                  {d.device_id ? d.device_id.slice(0, 18) + "…" : "—"}
                </td>
                <td className="p-3 font-mono text-xs text-white/60">{d.mac_address || "—"}</td>
                <td className="p-3">
                  <StatusBadge status={
                    d.status === "approved" ? "trusted" :
                    d.status === "pending" ? "warning" :
                    d.status === "revoked" ? "blocked" :
                    d.status === "declared" ? "info" : "info"
                  }>{d.status}</StatusBadge>
                </td>
                <td className="p-3 text-[10px] font-mono text-white/40">
                  {new Date(d.enrolled_at).toLocaleDateString()}
                </td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    {d.status !== "approved" && isAdmin && d.device_id && (
                      <Button size="icon" variant="ghost"
                              onClick={() => approve(d._id)}
                              data-testid={`approve-${d._id}`}
                              className="h-8 w-8 text-emerald-400 hover:bg-emerald-500/10">
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                    {d.status === "approved" && isAdmin && (
                      <Button size="icon" variant="ghost"
                              onClick={() => revoke(d._id)}
                              data-testid={`revoke-${d._id}`}
                              className="h-8 w-8 text-orange-400 hover:bg-orange-500/10">
                        <XCircle className="h-4 w-4" />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button size="icon" variant="ghost"
                              onClick={() => del(d._id)}
                              data-testid={`delete-device-${d._id}`}
                              className="h-8 w-8 text-rose-400 hover:bg-rose-500/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white"
                       data-testid="declare-dialog">
          <DialogHeader><DialogTitle>Pré-déclarer un terminal (inventaire)</DialogTitle></DialogHeader>
          <p className="text-sm text-white/60">
            Créez une entrée d'inventaire avec la MAC. À sa première connexion sur ce compte,
            l'utilisateur sera automatiquement lié à cette entrée et son terminal deviendra "géré".
          </p>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em]">Utilisateur</Label>
              <Select value={form.user_email}
                      onValueChange={(v) => setForm({ ...form, user_email: v })}>
                <SelectTrigger data-testid="dev-user-select" className="bg-white/[0.03] border-white/10 mt-1">
                  <SelectValue placeholder="Choisir…" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u._id} value={u.email}>{u.email} · {u.role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em]">Nom du terminal</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="dev-name" placeholder="MacBook Pro Marie C."
                     className="bg-white/[0.03] border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em]">Adresse MAC</Label>
              <Input value={form.mac_address}
                     onChange={(e) => setForm({ ...form, mac_address: e.target.value })}
                     data-testid="dev-mac" placeholder="AA:BB:CC:DD:EE:FF"
                     className="bg-white/[0.03] border-white/10 mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em]">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                     data-testid="dev-notes"
                     className="bg-white/[0.03] border-white/10 mt-1" />
            </div>
            <Button onClick={createDeclared} data-testid="dev-create-btn"
                    className="w-full bg-cyan-500 text-black hover:bg-cyan-400">
              Pré-déclarer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
